/**
 * DateRangeFilterDropdown Component
 *
 * A dropdown filter for selecting date ranges (First Seen / Last Seen).
 * Follows the pattern established by RangeSelectorDropdown for consistency.
 * Shared component used by both vulnerabilities and assets pages.
 */

import {
  ChevronDownIcon,
  PlusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import { Button } from '@/components/chariot-ui';
import React, { useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { StyledDatePicker } from '@/components/form/StyledDatePicker';
import { Popover } from '@/components/Popover';
import { DateRangeFilter } from '@/components/filters/types';
import { cn } from '@/utils/classname';

export interface DateRangeFilterDropdownProps {
  /** Label displayed on the trigger button */
  label: string;
  /** Current filter value */
  value?: DateRangeFilter;
  /** Callback when filter is applied or cleared */
  onApply?: (value: DateRangeFilter | undefined) => void;
}

/**
 * Date field options for the dropdown
 */
const DATE_FIELD_OPTIONS: Array<{
  value: DateRangeFilter['field'];
  label: string;
}> = [
  { value: 'created', label: 'First Seen' },
  { value: 'visited', label: 'Last Seen' },
];

/**
 * Formats a date range for display in the trigger button
 */
function formatDateRangeLabel(value: DateRangeFilter): string {
  const fieldLabel = value.field === 'created' ? 'First Seen' : 'Last Seen';
  return `${fieldLabel}: ${value.startDate} to ${value.endDate}`;
}

/**
 * DateRangeFilterDropdown allows users to filter by date range.
 * Users can select either "First Seen" (created) or "Last Seen" (visited) and
 * specify a start and end date.
 */
export function DateRangeFilterDropdown({
  label,
  value,
  onApply,
}: DateRangeFilterDropdownProps) {
  const [open, setOpenRaw] = useState(false);
  const [selectedField, setSelectedField] = useState<DateRangeFilter['field']>(
    value?.field || 'created'
  );
  const [startDate, setStartDate] = useState<string>(value?.startDate || '');
  const [endDate, setEndDate] = useState<string>(value?.endDate || '');
  const [validationError, setValidationError] = useState<string>('');

  const isActive = value !== undefined;

  // Track previous open state to detect transitions
  const prevOpenRef = useRef(false);

  // Wrap setOpen to reset local state when transitioning from closed to open
  const setOpen = (newOpen: boolean) => {
    // Only reset state when opening (transitioning false -> true)
    if (newOpen && !prevOpenRef.current) {
      setSelectedField(value?.field || 'created');
      setStartDate(value?.startDate || '');
      setEndDate(value?.endDate || '');
      setValidationError('');
    }
    prevOpenRef.current = newOpen;
    setOpenRaw(newOpen);
  };

  // Validate dates: start must be <= end
  const validateDates = (start: string, end: string): boolean => {
    if (!start || !end) {
      setValidationError('Both start and end dates are required');
      return false;
    }
    if (start > end) {
      setValidationError('Start date must be before or equal to end date');
      return false;
    }
    setValidationError('');
    return true;
  };

  const handleApply = () => {
    if (!validateDates(startDate, endDate)) {
      return;
    }

    onApply?.({
      field: selectedField,
      startDate,
      endDate,
    });
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onApply?.(undefined);
  };

  const selectedRangeLabel = value ? formatDateRangeLabel(value) : '';

  return (
    <Popover
      open={open}
      setOpen={setOpen}
      container={
        <button
          data-testid="date-range-filter-dropdown"
          type="button"
          className={twMerge(
            'flex max-w-full min-w-0 items-center rounded-full border border-dashed px-2 py-1 text-xs font-bold transition-colors duration-150',
            isActive
              ? 'border-chariot-brand-primary bg-chariot-background-secondary text-chariot-text-primary'
              : 'border-chariot-stroke text-chariot-text-secondary'
          )}
        >
          {isActive ? (
            <XCircleIcon
              className="text-chariot-text-primary mr-2 size-5 shrink-0"
              onClick={handleClear}
              data-testid="date-range-filter-clear"
            />
          ) : (
            <PlusCircleIcon className="text-chariot-text-secondary mr-2 size-5 shrink-0" />
          )}
          <span className="truncate">{label}</span>
          {isActive && (
            <>
              <span className="text-chariot-brand-primary mx-2">|</span>
              <span className="inline-block max-w-[200px] truncate">
                {selectedRangeLabel}
              </span>
              <ChevronDownIcon className="ml-2 size-4 shrink-0 text-current" />
            </>
          )}
        </button>
      }
    >
      <form
        className="flex w-80 flex-col gap-4"
        onSubmit={event => {
          event.preventDefault();
          handleApply();
        }}
      >
        {/* Field Selector */}
        <div>
          <span className="mb-2 block text-sm font-bold">Filter by date</span>
          <div className="flex gap-4">
            {DATE_FIELD_OPTIONS.map(option => (
              <label
                key={option.value}
                className={cn(
                  'flex cursor-pointer items-center gap-2 text-sm',
                  selectedField === option.value
                    ? 'text-chariot-text-primary'
                    : 'text-chariot-text-secondary'
                )}
              >
                <input
                  type="radio"
                  name="dateField"
                  value={option.value}
                  checked={selectedField === option.value}
                  onChange={() => setSelectedField(option.value)}
                  className="accent-chariot-brand-primary"
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        {/* Date Range Pickers */}
        <div className="flex gap-3">
          <div className="flex-1">
            <StyledDatePicker
              label="Start Date"
              name="startDate"
              value={startDate}
              onChange={setStartDate}
              max={endDate || undefined}
              className="w-full"
            />
          </div>
          <div className="flex-1">
            <StyledDatePicker
              label="End Date"
              name="endDate"
              value={endDate}
              onChange={setEndDate}
              min={startDate || undefined}
              className="w-full"
            />
          </div>
        </div>

        {/* Validation Error */}
        {validationError && (
          <p className="text-chariot-stroke-critical text-xs">
            {validationError}
          </p>
        )}

        {/* Apply Button */}
        <Button
          type="submit"
          label="Apply"
          size="xs"
          className="mt-1"
          onClick={() => {}}
        />
      </form>
    </Popover>
  );
}
